import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { CircularProgress } from '@mui/material'
import TableRow from '@mui/material/TableRow'
import debounce from 'lodash/debounce'
import { useSelector } from 'react-redux'
import { Redirect } from 'react-router-dom'
import { Paper, MotionDivContainer } from '@components'
import { Search } from '@components/search'
import { templateContext, submissionContext } from '@contexts'
import { useQueryString } from '@global/hooks'
import { getCellContent } from 'functions'
import { Toolbar as StyledToolbar } from 'styles/toolbar'
import * as styled from './styles'
import { BasicCell } from '../components/table/basic-cell'
import { CvsTable } from '../components/table/cvs-table'
import { SubmissionStatusCell } from '../submissions/submissions/components'
import { t } from '@lingui/core/macro'

export const Submissions = props => {
  const [isLoading, setIsLoading] = useState(true)
  const [searchLoading, setSearchLoading] = useState(true)
  const templateId = props.match.params.id
  const [redirectPath, setRedirectPath] = useState(null)
  const { byId, fetchSubmissions, submissionSummaryById } = useContext(submissionContext)
  const { fetchTemplate } = useContext(templateContext)
  const [template, setTemplate] = useState([])
  const [displaySubmissions, setDisplaySubmissions] = useState([])
  const [params, setParams] = useState({
    order_by: 'created_at',
    order: 'desc',
    offset: 0,
    template_id: templateId,
  })
  const { setUrl } = useQueryString(params, setParams)

  const submissionsWithSummary = useMemo(() => {
    return Object.values(byId).map(sub => ({
      ...byId[sub?.id],
      summary: submissionSummaryById[sub?.submission_id],
    }))
  }, [byId, submissionSummaryById])

  useEffect(() => {
    setDisplaySubmissions(submissionsWithSummary)
  }, [submissionsWithSummary])

  useEffect(() => {
    fetchSubmissions({
      template_id: templateId,
      order_by: 'created_at',
      order: 'desc',
      offset: 0,
    }).then(() => {
      setSearchLoading(false)
      setIsLoading(false)
    })

    fetchTemplate(templateId).then(templateData => {
      setTemplate(templateData)
    })
  }, [])

  const handleRowClick = row => {
    setRedirectPath(`/codes/details/${row.dataset_id}/data`)
  }

  const StatusComp = ({ row }) => (
    <SubmissionStatusCell submission={row} submissionSummary={row.summary} />
  )

  const debounced = useRef(
    debounce((val, temp) => {
      fetchSubmissions({
        ...params,
        offset: 0,
        term: val,
        template_id: temp,
      }).then(() => {
        setSearchLoading(false)
        setIsLoading(false)
      })
    }, 250),
  ).current

  const updateSearch = e => {
    const val = e.target.value
    setUrl({
      ...params,
      offset: 0,
      term: val,
    })
    setSearchLoading(true)
    setIsLoading(false)
    debounced(val, params.template_id)
  }

  const handleSort = sortBy => {
    setIsLoading(true)
    const updatedParams = {
      ...params,
      offset: 0,
    }
    if (sortBy == params.order_by) {
      updatedParams.order = params.order == 'asc' ? 'desc' : 'asc'
    } else {
      updatedParams.order_by = sortBy
    }
    setUrl(updatedParams)
    fetchSubmissions(updatedParams).then(() => {
      setSearchLoading(false)
      setIsLoading(false)
    })
  }

  const { features } = useSelector(state => state.Auth)
  const DATA_SOURCE_ENABLED = Boolean(features?.['show-data-source'])

  if (redirectPath) {
    return <Redirect push to={redirectPath} />
  }

  if (isLoading) {
    return (
      <MotionDivContainer>
        <styled.LoadingContainer>
          <CircularProgress />
        </styled.LoadingContainer>
      </MotionDivContainer>
    )
  }

  return (
    <MotionDivContainer>
      <Paper style={{ height: '100%', overflow: 'hidden' }} elevation={3}>
        <div style={{ height: 'calc(100% - 100px)' }}>
          <StyledToolbar>
            <styled.SearchContainer>
              <Search onChange={updateSearch} defaultValue={params.term} />
            </styled.SearchContainer>
          </StyledToolbar>
          {!searchLoading ? (
            <CvsTable
              headerRowPadding={50}
              rows={displaySubmissions}
              orderBy={params.order_by}
              sortOrder={params.order}
              keyField="dataset_id"
              noAdd
              noPagination
              style={{ maxWidth: '100%' }}
              customHandleSort={handleSort}
              emptyStateMessage={{
                icon: (
                  <styled.FabContainer disabled>
                    <styled.FileCopyOutlinedIconContainer />
                  </styled.FabContainer>
                ),
                title: t`No Submissions Found`,
                description: t`You can search for any submission created by yourself or any member of a group to which you belong`,
              }}
              columns={[
                { label: t`Submission Name`, width: '25%', lookup: 'submission_name' },
                {
                  label: t`Rows`,
                  width: '15%',
                  type: 'number',
                  lookup: 'rows',
                  blockSort: true,
                },
                { label: t`Created By`, width: '20%', lookup: 'created_by' },
                {
                  label: t`Last Submitted`,
                  width: '20%',
                  type: 'date',
                  lookup: 'created_at',
                },
                {
                  label: t`Status`,
                  width: '20%',
                  type: 'component',
                  lookup: 'status',
                  component: StatusComp,
                },
                [StatusComp],
              ]}
            >
              {({ row }) => {
                const approvalFlowEnabled = Boolean(template?.settings?.require_approval)
                const paddingLeftFirstCell = 50
                return (
                  <TableRow
                    hover
                    onClick={() => handleRowClick(row)}
                    tabIndex={-1}
                    key={row[props.keyField]}
                    style={{ cursor: 'pointer', maxWidth: '100%', overflow: 'hidden' }}
                  >
                    <BasicCell
                      cellData={row.submission_name}
                      title={row.submission_name}
                      style={{ paddingLeft: paddingLeftFirstCell }}
                    />
                    <BasicCell cellData={getCellContent('number', row.rows)} />
                    <BasicCell
                      cellData={
                        DATA_SOURCE_ENABLED ? (row.source ?? row.created_by) : row.created_by
                      }
                    />
                    <BasicCell
                      cellData={getCellContent('date', row.started_at || row.created_at)}
                    />
                    <BasicCell
                      cellData={
                        <SubmissionStatusCell
                          submission={row}
                          submissionSummary={row.summary}
                          approvalFlowEnabled={approvalFlowEnabled}
                        />
                      }
                    />
                  </TableRow>
                )
              }}
            </CvsTable>
          ) : (
            <styled.SearchLoadingContainer>
              <CircularProgress color="primary" />
            </styled.SearchLoadingContainer>
          )}
        </div>
      </Paper>
    </MotionDivContainer>
  )
}
